
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bot, X, Send, Sparkles, MessageSquare, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useWallet } from "@/contexts/WalletContext";
import { aiService } from "@/lib/ai";

interface Message {
    id: string;
    role: "user" | "assistant";
    content: string;
    isError?: boolean;
    retryContent?: string;
}

export function AIAssistant() {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        {
            id: "1",
            role: "assistant",
            content: "Hello! I'm your MoonRun trading assistant. I can help you analyze charts, execute trades, or set alerts. What's your move?",
        },
    ]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const { connected } = useWallet();

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isOpen]);

    const sendMessage = (messageContent: string, isRetry = false) => {
        if (isLoading) return;

        if (!isRetry) {
            const userMsg: Message = {
                id: Date.now().toString(),
                role: "user",
                content: messageContent,
            };
            setMessages((prev) => [...prev, userMsg]);
            setInput("");
        }

        setIsLoading(true);

        const context: { role: "system" | "user" | "assistant"; content: string }[] = [
            { role: "system", content: "You are the MoonRun Trading Assistant. If the user wants to trade, execute actions, or buy/sell, you MUST reply with a JSON object in this format (no other text): {\"action\": \"trade\", \"type\": \"buy\"|\"sell\", \"ticker\": \"SYMBOL\", \"amount\": number}. For general questions, reply with normal text. Be helpful, concise, and upbeat." },
            ...messages.map(m => ({ role: m.role, content: m.content })),
            { role: "user", content: messageContent }
        ];

        aiService.chat(context).then((responseText) => {
            const aiMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: "assistant",
                content: responseText || "I'm having trouble connecting to the network right now.",
            };
            setMessages((prev) => [...prev, aiMsg]);
        }).catch((error) => {
            const errorMsg: Message = {
                id: Date.now().toString(),
                role: "assistant",
                content: `Error: ${(error as Error).message}`,
                isError: true,
                retryContent: messageContent,
            };
            setMessages((prev) => [...prev, errorMsg]);
        }).finally(() => {
            setIsLoading(false);
        });
    };

    const handleSend = () => {
        if (!input.trim() || isLoading) return;
        sendMessage(input);
    };

    const handleRetry = (retryContent: string) => {
        sendMessage(retryContent, true);
    };

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="fixed bottom-24 right-6 w-96 max-h-[600px] h-[70vh] z-50 rounded-2xl border border-white/10 bg-card/90 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden ring-1 ring-white/5"
                        drag
                        dragConstraints={{ left: -1000, right: 0, top: -500, bottom: 0 }}
                        dragElastic={0.05}
                        dragMomentum={false}
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-white/5 bg-primary/5 flex items-center justify-between cursor-move" onPointerDownCapture={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-xl bg-primary/20 flex items-center justify-center border border-primary/20">
                                    <Bot className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-display font-bold">MoonRun Assistant</h3>
                                    <div className="flex items-center gap-1.5">
                                        <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
                                        <span className="text-xs text-muted-foreground">Online & Ready</span>
                                    </div>
                                </div>
                            </div>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setIsOpen(false)}>
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* Messages */}
                        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                            <div className="space-y-4">
                                {messages.map((msg) => {
                                    const isTradeAction = msg.content.startsWith('{"action":');
                                    let tradeData = null;
                                    try {
                                        if (isTradeAction) tradeData = JSON.parse(msg.content);
                                    } catch (e) {
                                        console.error("Failed to parse trade action data:", e);
                                    }

                                    return (
                                        <motion.div
                                            key={msg.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className={cn(
                                                "flex gap-3 max-w-[85%]",
                                                msg.role === "user" ? "ml-auto flex-row-reverse" : ""
                                            )}
                                        >
                                            <div className={cn(
                                                "h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold",
                                                msg.role === "user" ? "bg-secondary text-foreground" : "bg-primary/20 text-primary"
                                            )}>
                                                {msg.role === "user" ? "You" : <Bot className="h-4 w-4" />}
                                            </div>

                                            {isTradeAction && tradeData ? (
                                                <div className="bg-primary/10 border border-primary/20 rounded-2xl p-4 w-full">
                                                    <p className="text-sm font-semibold mb-2 text-foreground">
                                                        Prepare {tradeData.type.toUpperCase()} Order
                                                    </p>
                                                    <div className="flex justify-between items-center mb-3 text-xs bg-background/50 p-2 rounded">
                                                        <span>{tradeData.coin || tradeData.ticker}</span>
                                                        <span className="font-mono">{tradeData.amount} {tradeData.type === 'buy' ? 'SOL' : 'Tokens'}</span>
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        className="w-full"
                                                        onClick={() => {
                                                            if (connected) {
                                                                // Dispatch custom event for floating widget
                                                                const event = new CustomEvent('open-trade', {
                                                                    detail: {
                                                                        ticker: tradeData.ticker || tradeData.coin,
                                                                        type: tradeData.type,
                                                                        amount: tradeData.amount
                                                                    }
                                                                });
                                                                window.dispatchEvent(event);
                                                            }
                                                        }}
                                                    >
                                                        Review Order
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className={cn(
                                                    "p-3 rounded-2xl text-sm leading-relaxed",
                                                    msg.role === "user"
                                                        ? "bg-secondary text-foreground"
                                                        : msg.isError
                                                            ? "bg-destructive/10 text-destructive border border-destructive/20"
                                                            : "bg-primary/10 text-foreground border border-primary/10"
                                                )}>
                                                    {msg.content}
                                                    {msg.isError && msg.retryContent && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="mt-2 h-6 text-xs"
                                                            onClick={() => handleRetry(msg.retryContent!)}
                                                            disabled={isLoading}
                                                        >
                                                            <RefreshCw className="w-3 h-3 mr-1" />
                                                            Retry
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </motion.div>
                                    );
                                })}

                                {isLoading && (
                                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
                                        <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                                            <Bot className="h-4 w-4 text-primary" />
                                        </div>
                                        <div className="h-8 flex items-center gap-1 px-3 rounded-2xl bg-primary/5">
                                            <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]" />
                                            <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]" />
                                            <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" />
                                        </div>
                                    </motion.div>
                                )}
                            </div>
                        </ScrollArea>

                        {/* Input */}
                        <div className="p-4 border-t border-white/5 bg-background/50">
                            <div className="relative">
                                <Input
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                                    placeholder={connected ? "Ask me anything..." : "Connect wallet to trade..."}
                                    className="pr-12 bg-secondary/50 border-white/10 h-12 rounded-xl focus:ring-primary/20"
                                    disabled={isLoading || !connected}
                                    aria-label="Chat with AI assistant"
                                />
                                <Button
                                    size="icon"
                                    onClick={handleSend}
                                    disabled={!input.trim() || isLoading || !connected}
                                    className="absolute right-1 top-1 bottom-1 h-10 w-10 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground"
                                >
                                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                </Button>
                            </div>
                            <div className="mt-2 flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                                {["Trending Coin?", "Buy 1 SOL MDOGE", "My Portfolio"].map((suggestion) => (
                                    <button
                                        key={suggestion}
                                        onClick={() => { setInput(suggestion); }}
                                        className="text-[10px] px-2 py-1 rounded-full bg-secondary hover:bg-secondary/80 border border-white/5 text-muted-foreground whitespace-nowrap transition-colors"
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                            {!connected && (
                                <p className="mt-2 text-xs text-muted-foreground">
                                    Connect your wallet via the main UI to enable trading interactions.
                                </p>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                onClick={() => setIsOpen(!isOpen)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={cn(
                    "fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-2xl z-50 flex items-center justify-center transition-all duration-300",
                    isOpen ? "bg-secondary text-foreground rotate-90" : "bg-primary text-primary-foreground hover:shadow-[0_0_30px_hsl(var(--primary)/0.5)]"
                )}
            >
                {isOpen ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
            </motion.button>
        </>
    );
}
